/**
 * GameChat - Per-game live chat.
 * Plus users: text + emotes. Non-Plus users: emotes only.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, Send, Trash2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMOTES = [
  { id: "angry", label: "Angry", src: "/emotes/angry.png" },
  { id: "cheer", label: "Cheer", src: "/emotes/cheer.png" },
  { id: "confused", label: "Confused", src: "/emotes/confused.png" },
  { id: "love", label: "Love", src: "/emotes/love.png" },
  { id: "sad", label: "Sad", src: "/emotes/sad.png" },
];

const GameChat = ({ broadcastId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [isPlus, setIsPlus] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showEmotes, setShowEmotes] = useState(false);
  const messagesEndRef = useRef(null);
  const lastMsgId = useRef(null);
  const pollRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Check auth
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    const adminToken = localStorage.getItem("auth_token");
    if (token) {
      setIsLoggedIn(true);
      axios.get(`${API}/users/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setIsPlus(!!res.data.is_plus);
          setUserId(res.data.id);
        })
        .catch(() => {});
    }
    if (adminToken) setIsAdmin(true);
  }, []);

  // Initial load
  useEffect(() => {
    if (!broadcastId) return;
    axios.get(`${API}/game-chat/${broadcastId}/messages?limit=50`)
      .then(res => {
        setMessages(res.data.messages || []);
        const msgs = res.data.messages || [];
        if (msgs.length > 0) lastMsgId.current = msgs[msgs.length - 1].id;
      })
      .catch(() => {});
  }, [broadcastId]);

  // Poll for new messages
  useEffect(() => {
    if (!broadcastId) return;
    const poll = () => {
      const url = lastMsgId.current
        ? `${API}/game-chat/${broadcastId}/messages/poll?after=${lastMsgId.current}`
        : `${API}/game-chat/${broadcastId}/messages/poll`;
      axios.get(url)
        .then(res => {
          const newMsgs = res.data.messages || [];
          if (newMsgs.length > 0) {
            setMessages(prev => [...prev, ...newMsgs]);
            lastMsgId.current = newMsgs[newMsgs.length - 1].id;
          }
        })
        .catch(() => {});
    };
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
  }, [broadcastId]);

  // Auto-scroll
  useEffect(() => {
    const c = chatContainerRef.current;
    if (!c) return;
    if (c.scrollHeight - c.scrollTop - c.clientHeight < 80) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const sendText = useCallback(async () => {
    if (!input.trim() || sending) return;
    const token = localStorage.getItem("userToken");
    if (!token) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/game-chat/${broadcastId}/messages`,
        { content: input.trim(), type: "text" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setMessages(prev => [...prev, res.data.message]);
        lastMsgId.current = res.data.message.id;
        setInput("");
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } catch (err) {
      console.error("Send failed:", err);
    }
    setSending(false);
  }, [input, sending, broadcastId]);

  const sendEmote = useCallback(async (emoteId) => {
    if (sending) return;
    const token = localStorage.getItem("userToken");
    if (!token) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/game-chat/${broadcastId}/messages`,
        { content: emoteId, type: "emote" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setMessages(prev => [...prev, res.data.message]);
        lastMsgId.current = res.data.message.id;
        setShowEmotes(false);
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } catch (err) {
      console.error("Emote failed:", err);
    }
    setSending(false);
  }, [sending, broadcastId]);

  const deleteMessage = useCallback(async (messageId) => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    try {
      await axios.delete(`${API}/game-chat/${broadcastId}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }, [broadcastId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText();
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  const getEmoteSrc = (emoteId) => {
    const emote = EMOTES.find(e => e.id === emoteId);
    return emote ? emote.src : null;
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden" data-testid="game-chat">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-3 py-2.5 border-b border-white/10 hover:bg-white/5 transition-colors"
        data-testid="game-chat-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-orange-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-white">Game Chat</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500">{messages.length}</span>
          <span className="text-zinc-500 text-xs">{collapsed ? "+" : "-"}</span>
        </div>
      </button>

      {!collapsed && (
        <>
          {/* Messages */}
          <div
            ref={chatContainerRef}
            className="overflow-y-auto p-2 space-y-0.5 min-h-[180px] max-h-[320px]"
            data-testid="game-chat-messages"
          >
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <MessageCircle className="w-8 h-8 text-zinc-700 mb-2" />
                <p className="text-zinc-600 text-xs">No messages yet</p>
                <p className="text-zinc-700 text-[10px]">Be the first to chat!</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`group px-2 py-1 rounded hover:bg-white/5 transition-colors ${
                    msg.user_id === userId ? "bg-orange-500/5" : ""
                  }`}
                  data-testid={`chat-message-${msg.id}`}
                >
                  <div className="flex items-start gap-1.5">
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center gap-1">
                        <span className={`text-[11px] font-bold ${
                          msg.role === "developer" ? "text-orange-400" :
                          msg.is_plus ? "text-amber-400" : "text-zinc-400"
                        }`}>
                          {msg.display_name || msg.username}
                        </span>
                        {msg.is_plus && (
                          <img src="/asn-plus-logo.png" alt="ASN+" className="w-3.5 h-3.5 object-contain" />
                        )}
                        <span className="text-[9px] text-zinc-600">{formatTime(msg.timestamp)}</span>
                      </span>
                      {msg.type === "emote" ? (
                        <div className="mt-0.5">
                          <img
                            src={getEmoteSrc(msg.content)}
                            alt={msg.content}
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                      ) : (
                        <p className="text-zinc-300 text-xs leading-relaxed break-words">{msg.content}</p>
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => deleteMessage(msg.id)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-zinc-600 hover:text-red-400 transition-all shrink-0"
                        title="Delete message"
                        data-testid={`delete-msg-${msg.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Emote picker */}
          {showEmotes && (
            <div className="border-t border-white/10 px-3 py-2 bg-zinc-800/50" data-testid="emote-picker">
              <div className="flex items-center justify-center gap-2">
                {EMOTES.map((emote) => (
                  <button
                    key={emote.id}
                    onClick={() => sendEmote(emote.id)}
                    disabled={sending}
                    className="w-10 h-10 p-1 rounded-lg hover:bg-white/10 active:scale-90 transition-all disabled:opacity-50"
                    title={emote.label}
                    data-testid={`emote-btn-${emote.id}`}
                  >
                    <img src={emote.src} alt={emote.label} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-white/10 p-2">
            {isLoggedIn ? (
              isPlus ? (
                <div className="flex items-center gap-1.5" data-testid="game-chat-input-area">
                  <button
                    onClick={() => setShowEmotes(!showEmotes)}
                    className={`p-1.5 rounded transition-colors shrink-0 ${
                      showEmotes ? "bg-orange-500/20 text-orange-400" : "text-zinc-500 hover:text-zinc-300 hover:bg-white/5"
                    }`}
                    title="Emotes"
                    data-testid="emote-toggle"
                  >
                    <img src="/emotes/cheer.png" alt="emotes" className="w-5 h-5 object-contain" />
                  </button>
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    maxLength={500}
                    className="flex-1 bg-zinc-800 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500/50 min-w-0"
                    data-testid="game-chat-input"
                  />
                  <button
                    onClick={sendText}
                    disabled={!input.trim() || sending}
                    className="p-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-30 disabled:hover:bg-orange-500 rounded text-white transition-colors shrink-0"
                    data-testid="game-chat-send"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div data-testid="game-chat-emote-only">
                  <div className="flex items-center justify-center gap-2 py-1">
                    {EMOTES.map((emote) => (
                      <button
                        key={emote.id}
                        onClick={() => sendEmote(emote.id)}
                        disabled={sending}
                        className="w-9 h-9 p-0.5 rounded-lg hover:bg-white/10 active:scale-90 transition-all disabled:opacity-50"
                        title={emote.label}
                        data-testid={`emote-only-btn-${emote.id}`}
                      >
                        <img src={emote.src} alt={emote.label} className="w-full h-full object-contain" />
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-[10px] text-zinc-600 mt-0.5">
                    <a href="/asn-plus" className="text-orange-400 hover:text-orange-300 font-semibold">ASN Plus</a>
                    {" "}members can send text messages
                  </p>
                </div>
              )
            ) : (
              <div className="text-center py-2" data-testid="game-chat-login-prompt">
                <p className="text-[11px] text-zinc-500">
                  <a href="/login-user" className="text-orange-400 hover:text-orange-300 font-semibold">Sign in</a>
                  {" "}to join the chat
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { GameChat };
export default GameChat;
